import type {
  SyncEngineConfig, PushOp, SyncChange,
  PushResult, PullResult, SyncResult,
} from './types';

// Cloud API response shapes (not exported — internal to the engine)
interface ConnectResponse { deviceId: string; cursor: string }
interface PushResponse { accepted: string[]; rejected: { id: string; reason: string }[] }
interface PullResponse { changes: SyncChange[]; cursor: string }

export class SyncEngine {
  private readonly cfg: Required<Pick<SyncEngineConfig, 'apiUrl' | 'deviceName' | 'batchSize'>>
    & Omit<SyncEngineConfig, 'apiUrl' | 'deviceName' | 'batchSize'>;

  constructor(config: SyncEngineConfig) {
    this.cfg = {
      deviceName: 'Desktop',
      batchSize: 100,
      ...config,
    };
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /** True when a deviceId is stored (i.e. connect() has been called). */
  async isConnected(): Promise<boolean> {
    return !!(await this.cfg.storage.getDeviceId());
  }

  /**
   * Register this device with the cloud.
   * Stores the returned deviceId and initial cursor so future push/pull calls work.
   * Safe to call multiple times — if already connected the stored values are refreshed.
   */
  async connect(): Promise<void> {
    const token = this.requireToken();
    this.log('info', 'Connecting device to cloud…');

    const res = await this.post<ConnectResponse>('/sync/connect', token, {
      deviceName: this.cfg.deviceName,
    });

    await this.cfg.storage.setDeviceId(res.deviceId);
    await this.cfg.storage.setCursor(res.cursor);
    this.log('info', `Connected — deviceId=${res.deviceId}, cursor=${res.cursor}`);
  }

  /**
   * Push all pending local operations to the cloud.
   * Ops are sent in batches of cfg.batchSize.
   * Each batch is idempotent — safe to retry after a failure.
   */
  async push(): Promise<PushResult> {
    const token = this.requireToken();
    const deviceId = await this.requireDeviceId();

    const pending = await this.cfg.localDb.getPendingOps();
    if (pending.length === 0) {
      this.log('info', 'Push: nothing pending');
      return { pushed: 0, rejected: 0, rejectedOps: [] };
    }

    this.log('info', `Push: ${pending.length} pending op(s)`);

    let totalPushed = 0;
    let totalRejected = 0;
    const allRejected: { id: string; reason: string }[] = [];

    for (let i = 0; i < pending.length; i += this.cfg.batchSize) {
      const batch: PushOp[] = pending.slice(i, i + this.cfg.batchSize);

      const res = await this.post<PushResponse>('/sync/push', token, {
        deviceId,
        ops: batch,
      });

      if (res.accepted.length > 0) {
        await this.cfg.localDb.markOpsAsSynced(res.accepted);
        totalPushed += res.accepted.length;
      }

      if (res.rejected.length > 0) {
        totalRejected += res.rejected.length;
        allRejected.push(...res.rejected);
        for (const r of res.rejected) {
          this.log('warn', `Push: op ${r.id} rejected — ${r.reason}`);
        }
      }
    }

    this.log('info', `Push complete — accepted=${totalPushed}, rejected=${totalRejected}`);
    return { pushed: totalPushed, rejected: totalRejected, rejectedOps: allRejected };
  }

  /**
   * Pull all changes from the cloud since the stored cursor.
   * Applies each change by calling cfg.localDb.applyChange().
   * Updates the cursor on success.
   */
  async pull(): Promise<PullResult> {
    const token = this.requireToken();
    const cursor = (await this.cfg.storage.getCursor()) ?? new Date(0).toISOString();

    this.log('info', `Pull: since ${cursor}`);

    const res = await this.get<PullResponse>(
      `/sync/pull?since=${encodeURIComponent(cursor)}`,
      token,
    );

    if (res.changes.length === 0) {
      this.log('info', 'Pull: up to date');
      await this.cfg.storage.setCursor(res.cursor);
      return { pulled: 0 };
    }

    this.log('info', `Pull: applying ${res.changes.length} change(s)`);

    for (const change of res.changes) {
      try {
        await this.cfg.localDb.applyChange(change);
      } catch (e) {
        this.log('error', `Pull: failed to apply ${change.entity}/${change.entityId} — ${e instanceof Error ? e.message : String(e)}`);
      }
    }

    await this.cfg.storage.setCursor(res.cursor);
    this.log('info', `Pull complete — ${res.changes.length} change(s), cursor=${res.cursor}`);
    return { pulled: res.changes.length };
  }

  /**
   * Full sync cycle: push local ops first, then pull remote changes.
   * Push-before-pull reduces conflict probability (cloud sees our latest state first).
   */
  async sync(): Promise<SyncResult> {
    const push = await this.push();
    const pull = await this.pull();
    return { ...push, pulled: pull.pulled };
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private requireToken(): string {
    const token = this.cfg.getAuthToken();
    if (!token) throw new Error('SyncEngine: not authenticated — getAuthToken() returned null');
    return token;
  }

  private async requireDeviceId(): Promise<string> {
    const id = await this.cfg.storage.getDeviceId();
    if (!id) throw new Error('SyncEngine: device not connected — call connect() first');
    return id;
  }

  private async post<T>(path: string, token: string, body: unknown): Promise<T> {
    const res = await fetch(`${this.cfg.apiUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`SyncEngine: POST ${path} failed ${res.status} — ${text}`);
    }
    return res.json() as Promise<T>;
  }

  private async get<T>(path: string, token: string): Promise<T> {
    const res = await fetch(`${this.cfg.apiUrl}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`SyncEngine: GET ${path} failed ${res.status} — ${text}`);
    }
    return res.json() as Promise<T>;
  }

  private log(level: 'info' | 'warn' | 'error', message: string) {
    this.cfg.onLog?.(level, message);
  }
}
